import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/core/firebase'
import { paths } from '@/db/paths'
import { showLoading, hideLoading } from '@/core/ui'
import { formatCurrency, formatNumber } from '@/utils/string/numbers'

const viewChoiceModal = document.getElementById('view-choice-modal')
const dailyModal = document.getElementById('summary-modal')
const monthlyModal = document.getElementById('monthly-summary-modal')

export function openViewChoiceModal(uploadId: string, fileName: string, branchId: string) {
  if (!viewChoiceModal) return
  ;(viewChoiceModal as HTMLElement).dataset.uploadId = uploadId
  ;(viewChoiceModal as HTMLElement).dataset.branchId = branchId
  ;(viewChoiceModal as HTMLElement).dataset.fileName = fileName
  viewChoiceModal.classList.remove('hidden')
}

export function initializeViewChoiceListeners() {
  const dailyBtn = document.getElementById('view-choice-daily-btn')
  const monthlyBtn = document.getElementById('view-choice-monthly-btn')
  const cancelBtn = document.getElementById('view-choice-cancel-btn')
  const closeBtn = document.getElementById('view-choice-modal-close')

  const closeViewChoice = () => viewChoiceModal?.classList.add('hidden')

  cancelBtn?.addEventListener('click', closeViewChoice)
  closeBtn?.addEventListener('click', closeViewChoice)

  dailyBtn?.addEventListener('click', async () => {
    const { uploadId, branchId } = (viewChoiceModal as HTMLElement).dataset
    if (uploadId && branchId) {
      closeViewChoice()
      await loadAndShowDailyRecap(uploadId, branchId)
    }
  })

  monthlyBtn?.addEventListener('click', async () => {
    const { uploadId, branchId } = (viewChoiceModal as HTMLElement).dataset
    if (uploadId && branchId) {
      closeViewChoice()
      await loadAndShowMonthlySummary(uploadId, branchId)
    }
  })

  document.getElementById('summary-modal-close')?.addEventListener('click', () => dailyModal?.classList.add('hidden'))
  document.getElementById('monthly-summary-modal-close')?.addEventListener('click', () => monthlyModal?.classList.add('hidden'))
}

async function fetchFullReportData(uploadId: string, branchId: string) {
  showLoading({ message: 'Fetching report details...' })
  try {
    const reportRef = doc(db, paths.branches(branchId).salesReports.collectionPath, uploadId)
    const reportSnap = await getDoc(reportRef)
    
    if (!reportSnap.exists()) throw new Error('Report document not found')
    
    const metadata = reportSnap.data()
    const period = metadata.period 

    if (!period) throw new Error('Report has no period field')

    // Query flat collection 'salesDailySummaries'
    const summariesRef = collection(db, paths.branches(branchId).salesDailySummaries.collectionPath)
    const q = query(
        summariesRef,
        where('date', '>=', `${period}-01`),
        where('date', '<=', `${period}-31`)
    )

    const summariesSnap = await getDocs(q)
    const summaries = summariesSnap.docs.map(d => d.data())
    
    return { metadata, summaries }
  } catch (error) {
    console.error('[ERROR] Fetching report:', error)
    alert('Failed to load report data.')
    return null
  } finally {
    hideLoading()
  }
}

// --- Renderers ---

async function loadAndShowDailyRecap(uploadId: string, branchId: string) {
  const result = await fetchFullReportData(uploadId, branchId)
  if (!result) return

  const { summaries } = result
  const container = document.getElementById('daily-recap-container')
  if (!container) return

  if (!summaries || summaries.length === 0) {
    container.innerHTML = '<p class="text-gray-500 text-center p-4">No daily summaries found. The file might still be processing.</p>'
  } else {
    // Sort by date
    summaries.sort((a: any, b: any) => {
        const dateA = new Date(a.date).getTime()
        const dateB = new Date(b.date).getTime()
        return dateA - dateB
    })

    container.innerHTML = summaries.map((day: any) => {
      const dateObj = new Date(day.date)
      const dateStr = dateObj.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long' })
      const omzet = formatCurrency(day.totalOmzet || 0)
      
      // Attempt to find top items for the mini-table
      // We look at ALL categories in the daily summary
      const allItems: Record<string, number> = {}
      if (day.menuItemQuantities) {
        Object.values(day.menuItemQuantities).forEach((catGroup: any) => {
            Object.entries(catGroup).forEach(([item, qty]) => {
                allItems[item] = (allItems[item] || 0) + (qty as number)
            })
        })
      }

      return `
        <div class="border rounded-md mb-2 overflow-hidden">
          <button class="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 text-left transition-colors" onclick="this.nextElementSibling.classList.toggle('hidden')">
            <span class="font-medium text-gray-700">${dateStr}</span>
            <span class="font-bold text-gray-900">${omzet}</span>
          </button>
          <div class="hidden p-4 bg-white border-t">
            <div class="grid grid-cols-2 gap-4 text-sm mb-4">
               <div><span class="text-gray-500">Transactions:</span> <span class="font-medium">${day.totalTransactions}</span></div>
               <div><span class="text-gray-500">Avg Check:</span> <span class="font-medium">${formatCurrency(day.apc || 0)}</span></div>
               <div><span class="text-gray-500">Items Sold:</span> <span class="font-medium">${day.totalItemsSold || 0}</span></div>
            </div>
            ${renderMiniTable('Top Items', allItems, 3)}
          </div>
        </div>
      `
    }).join('')
  }

  dailyModal?.classList.remove('hidden')
}

async function loadAndShowMonthlySummary(uploadId: string, branchId: string) {
  const result = await fetchFullReportData(uploadId, branchId)
  if (!result) return

  const { summaries } = result
  const set = (id: string, val: string) => { const el = document.getElementById(id); if(el) el.textContent = val }
  
  if (summaries.length === 0) {
      alert("This report contains no daily data yet. Please wait for processing.")
      return
  }

  // 1. CALCULATE AGGREGATES
  const totalOmzet = summaries.reduce((a: number, b: any) => a + (b.totalOmzet || 0), 0)
  const totalTrans = summaries.reduce((a: number, b: any) => a + (b.totalTransactions || 0), 0)
  const totalItems = summaries.reduce((a: number, b: any) => a + (b.totalItemsSold || 0), 0)
  const apc = totalTrans ? totalOmzet / totalTrans : 0

  set('monthly-total-omzet', formatCurrency(totalOmzet))
  set('monthly-total-transactions', formatNumber(totalTrans))
  set('monthly-apc', formatCurrency(apc))
  set('monthly-total-items', formatNumber(totalItems))
  
  // 2. DATA SCOPE
  const dates = summaries.map((s: any) => new Date(s.date).getTime())
  const min = new Date(Math.min(...dates))
  const max = new Date(Math.max(...dates))
  set('monthly-date-range', `${min.toLocaleDateString()} - ${max.toLocaleDateString()}`)
  set('monthly-total-days', summaries.length.toString())
  set('monthly-branch-count', '1')

  // 3. TOP ITEMS AGGREGATION (FUZZY MATCHING)
  // Collect all unique categories found in the data for debugging
  const foundCategories = new Set<string>();

  const aggregateItems = (type: 'food' | 'drink') => {
      const map: Record<string, number> = {}
      
      // Extended keywords for fuzzy matching
      const foodKeywords = ['makan', 'food', 'snack', 'roti', 'cake', 'dessert', 'pastry', 'bakery', 'kitchen', 'main', 'appetizer', 'soup', 'rice', 'noodle', 'porridge', 'vegetable', 'meat', 'chicken', 'fish', 'seafood', 'promo makanan', 'food promo'];
      const drinkKeywords = ['minum', 'drink', 'beverage', 'coffee', 'kopi', 'tea', 'teh', 'juice', 'jus', 'milk', 'soda', 'water', 'air', 'bar', 'wine', 'beer', 'alcohol', 'latte', 'espresso', 'frappe', 'promo minuman', 'drink promo'];

      const keywords = type === 'food' ? foodKeywords : drinkKeywords;

      summaries.forEach((s: any) => {
          const root = s.menuItemQuantities || {}
          Object.keys(root).forEach(catKey => {
              foundCategories.add(catKey); // Log category
              
              // Check if category name matches any keyword (case insensitive)
              const lowerKey = catKey.toLowerCase();
              const isMatch = keywords.some(k => lowerKey.includes(k));

              if (isMatch) {
                  const catData = root[catKey] || {}
                  Object.entries(catData).forEach(([k, v]) => {
                      map[k] = (map[k] || 0) + (v as number)
                  })
              }
          })
      })
      return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 5)
  }

  const topFood = aggregateItems('food')
  const topBev = aggregateItems('drink')

  // Debugging log to see what categories exist in your file
  console.log('[DEBUG] Categories found in file:', Array.from(foundCategories));

  const renderList = (id: string, items: [string, number][]) => {
      const el = document.getElementById(id)
      if(!el) return
      if (items.length === 0) el.innerHTML = '<li class="text-gray-400 italic text-xs">No matching items found</li>'
      else el.innerHTML = items.map(([k, v]) => `<li class="flex justify-between border-b border-gray-100 py-1"><span>${k}</span><span class="font-mono text-gray-600">${v}</span></li>`).join('')
  }

  renderList('monthly-top-makanan', topFood)
  renderList('monthly-top-minuman', topBev)

  // 4. CHANNELS
  const channels: Record<string, number> = {}
  summaries.forEach((s: any) => {
      const visitData = s.revenueByVisitPurpose || s.salesByChannel || {}
      Object.entries(visitData).forEach(([k, v]) => channels[k] = (channels[k] || 0) + (v as number))
  })
  
  const channelList = Object.entries(channels).sort((a, b) => b[1] - a[1])
  renderList('monthly-bills-by-channel', channelList.map(([k, v]) => [k, formatCurrency(v)]))

  // 5. PEAK PERFORMANCE
  let busiestDay = { date: '', omzet: 0 }
  const hourlyTraffic: number[] = new Array(24).fill(0)

  summaries.forEach((s: any) => {
      if (s.totalOmzet > busiestDay.omzet) {
          const d = new Date(s.date)
          busiestDay = { date: d.toLocaleDateString('id-ID', { weekday: 'short', day: 'numeric' }), omzet: s.totalOmzet }
      }
      if (s.trafficByHour) {
          s.trafficByHour.forEach((val: number, idx: number) => hourlyTraffic[idx] += val)
      }
  })

  const peakHourIdx = hourlyTraffic.indexOf(Math.max(...hourlyTraffic))
  const peakHourStr = `${String(peakHourIdx).padStart(2, '0')}:00 - ${String(peakHourIdx+1).padStart(2, '0')}:00`

  const branchRevContainer = document.getElementById('monthly-revenue-by-branch')?.parentElement
  if (branchRevContainer) {
      const header = branchRevContainer.querySelector('h4')
      if(header) header.textContent = 'Peak Performance'
      
      const list = branchRevContainer.querySelector('ul') || document.getElementById('monthly-revenue-by-branch')
      if(list) {
          list.innerHTML = `
            <li class="mb-4">
                <span class="text-xs text-gray-500 uppercase font-semibold block mb-1">Busiest Day</span>
                <div class="flex justify-between items-baseline border-b border-gray-100 pb-2">
                    <span class="font-medium text-gray-800">${busiestDay.date || 'N/A'}</span>
                    <span class="font-mono text-green-600 font-bold">${formatCurrency(busiestDay.omzet)}</span>
                </div>
            </li>
            <li>
                <span class="text-xs text-gray-500 uppercase font-semibold block mb-1">Peak Hour (Traffic)</span>
                <div class="flex justify-between items-baseline">
                    <span class="font-medium text-gray-800">${peakHourStr}</span>
                    <span class="text-xs text-gray-500">Based on historical avg</span>
                </div>
            </li>
          `
      }
  }

  monthlyModal?.classList.remove('hidden')
}

function renderMiniTable(title: string, data: Record<string, number>, limit: number) {
    const sorted = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, limit)
    if(sorted.length === 0) return ''
    
    return `
        <div class="mt-2">
            <p class="text-xs font-semibold text-gray-500 uppercase">${title}</p>
            <ul class="text-xs mt-1 space-y-1">
                ${sorted.map(([k, v]) => `<li class="flex justify-between"><span>${k}</span><span>${v}</span></li>`).join('')}
            </ul>
        </div>
    `
}